import { motion } from 'framer-motion';

import { Box, BoxProps, useMediaQuery } from '@vimeo/bokeh';

import {
  NAV_HEIGHT,
  REVEAL_HEIGHT,
  REVEAL_HEIGHT_LARGE,
  SHOWCASE_REVEAL_HEIGHT,
  SHOWCASE_REVEAL_HEIGHT_LARGE,
} from '../constants';
import { useIntersectionVisibility } from '../hooks/use-intersection-visibility';
import { useLayout } from '../hooks/use-layout';

type Props = BoxProps & {
  thumbnailUrl?: string | null;
  aspectRatio?: string;
  backgroundColor?: string;
  globalNavigationHidden?: boolean;
  isSticky?: boolean;
  outerContainerHeight?: string;
};

/**
 * A wrapping element that will control the player's sticky behavior and aspect ratio.
 * In theatre mode the player needs to ignore the page maxW and be full browser width & switch to sticky past a certain scroll threshold.
 * Out of theatre mode the player should respect the page maxW and be constrained by the videos aspect ratio.
 */
export const PlayerWrapper = ({
  children,
  thumbnailUrl,
  backgroundColor,
  aspectRatio,
  globalNavigationHidden = false,
  isSticky = true,
  outerContainerHeight = '100vh',
  ...rest
}: React.PropsWithChildren<Props>) => {
  const [isLandscape] = useMediaQuery('(orientation: landscape)');
  const playerBg = backgroundColor || 'surface';
  const { isShowcaseLayout, isSideBySideActive } = useLayout();
  const { isVisible, elementRef } = useIntersectionVisibility(240 + NAV_HEIGHT);

  const isStickyPlayer = isSticky && !isVisible && !isSideBySideActive;
  const stickyPlayerPosition = globalNavigationHidden ? 0 : NAV_HEIGHT;

  const revealHeight = isShowcaseLayout ? SHOWCASE_REVEAL_HEIGHT : REVEAL_HEIGHT;
  const revealHeightLarge = isShowcaseLayout ? SHOWCASE_REVEAL_HEIGHT_LARGE : REVEAL_HEIGHT_LARGE;

  const outerContainerMinH = {
    base: '200px',
    lg: '420px',
    xl: isStickyPlayer ? '240px' : '420px',
  };

  const outerContainerMaxH = {
    base: isLandscape ? `calc(100vh - ${NAV_HEIGHT}px)` : `calc(100vh - ${revealHeight}px)`,
    md: `calc(100vh - ${revealHeight}px)`,
    xl: `calc(100vh - ${revealHeightLarge}px)`,
  };

  return (
    <Box
      // Outer container, relatively positioned so it's height will inform the grid row height.
      // Required to correctly position the second row of the grid
      ref={elementRef}
      as={motion.div}
      key="player-wrapper"
      w="100%"
      h={{ base: '100%', md: isSideBySideActive ? '100%' : outerContainerHeight }}
      minH={outerContainerMinH}
      maxH={outerContainerMaxH}
      aspectRatio={aspectRatio ?? '16/9'}
      borderRadius={{ base: 0, md: isSideBySideActive ? 'drawer' : 0 }}
      overflow="hidden"
      {...(isShowcaseLayout && {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      })}
      {...rest}
    >
      <Box
        // Inner container, absolutely positioned to the clip page base (nearest relatively positioned).
        // Allows the player to position relative to the page instead of the grid (full width ignoring grid max-width or sticky)
        // Sticky player is only active xl and above + not in side by side so styles are applied conditionally at that bp
        w="100%"
        h={{ base: 'inherit', xl: isStickyPlayer ? '240px' : 'inherit' }}
        minH={{ base: 'inherit', xl: isStickyPlayer ? '240px' : 'inherit' }}
        maxH="inherit" // NOTE: (when visiting a vertical video on mobile) mobile safari does not respect parent container max height, this Box will stretch and fold content that exceeds parent container max height
        backgroundColor={isSideBySideActive ? 'transparent' : playerBg}
        position={
          isSideBySideActive
            ? 'unset'
            : { base: 'relative', md: 'absolute', xl: isStickyPlayer ? 'fixed' : 'absolute' }
        }
        top={{ base: 0, xl: isStickyPlayer ? stickyPlayerPosition : 0 }}
        left={0}
        bgSize="contain"
        bgPosition="center"
        bgRepeat="no-repeat"
        zIndex={2}
        {...(thumbnailUrl && { bgImage: `url(${thumbnailUrl})` })}
        sx={{
          'button#chapters-control-bar-button': {
            display: 'none',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
